import { useEffect, useState } from "react";

/* Lightweight in-app session id (per browser tab) — used to group pageviews
   so we can compute average session time. */
const KEY = "clinic-os-session-id";

function readUtms() {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      utm_source: sp.get("utm_source") || undefined,
      utm_medium: sp.get("utm_medium") || undefined,
      utm_campaign: sp.get("utm_campaign") || undefined,
      utm_term: sp.get("utm_term") || undefined,
      utm_content: sp.get("utm_content") || undefined,
    };
  } catch {
    return {};
  }
}

export function getSessionId() {
  if (typeof sessionStorage === "undefined") return "no-storage";
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

export function attributionPayload(extra = {}) {
  let landing = "";
  try {
    landing = sessionStorage.getItem("clinic-os-landing") || window.location.pathname;
    if (!sessionStorage.getItem("clinic-os-landing")) {
      sessionStorage.setItem("clinic-os-landing", landing);
    }
  } catch {
    landing = window.location.pathname;
  }
  return {
    landing_page: landing,
    page: window.location.pathname,
    referrer: document.referrer || undefined,
    ...readUtms(),
    ...extra,
  };
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useTracker(path, title) {
  useEffect(() => {
    // Don't track admin / access routes
    if (path && path.startsWith("/access")) return;

    const load_ms = Math.round(
      (typeof performance !== "undefined" && performance.now()) || 0
    );

    const body = {
      session_id: getSessionId(),
      path: path || window.location.pathname,
      title: title || document.title,
      referrer: document.referrer || undefined,
      user_agent: navigator.userAgent,
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      language: navigator.language,
      load_ms,
      event: "pageview",
      ...readUtms(),
    };

    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  }, [path, title]);
}

/* Hook used by Layout — fires on every route change */
export function useRouteTracker(pathname) {
  const [title, setTitle] = useState(document.title);
  useEffect(() => setTitle(document.title), [pathname]);
  useTracker(pathname, title);
}

/* Fire-and-forget custom event (path-card click, button click, etc).
   Uses navigator.sendBeacon when available (survives immediate navigation),
   falls back to fetch+keepalive otherwise. */
export function trackEvent(event, { persona, meta } = {}) {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith("/access")) return;
  const body = {
    session_id: getSessionId(),
    path: window.location.pathname,
    title: document.title,
    referrer: document.referrer || undefined,
    user_agent: navigator.userAgent,
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    language: navigator.language,
    event,
    persona,
    meta,
    ...readUtms(),
  };

  const url = `${API}/track`;
  const payload = JSON.stringify(body);

  // Prefer sendBeacon — survives page navigation reliably.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch (_) {
    /* fall through to fetch */
  }

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}
